/* eslint-disable no-restricted-syntax */
import { ApolloClient, ApolloLink, from } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { RestLink } from 'apollo-link-rest';

import { onError } from 'apollo-link-error';
import { accessSuspendedVar, authState, appState } from 'core/state';
import { NetworkErrors } from 'context';
import { MeDocument } from 'core/graphql/generated/hooks';

import { cache } from '../cache';

const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_SCHEMA_PATH,
});

const restLink = new RestLink({
  uri: process.env.REACT_APP_REST_PATH,
  endpoints: {
    resume: {
      uri: `${process.env.REACT_APP_REST_PATH}/resume/download_resume`,
      responseTransformer: async (response: any) =>
        response
          .blob()
          .then((blob: Blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'Resume.pdf';
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
          })
          .then((error: any) => error),
    },
  },
});

const middlewareLink = new ApolloLink((operation, forward) => {
  const { access_token } = authState();
  const oldHeaders = operation.getContext().headers;
  if (access_token) {
    operation.setContext({
      headers: {
        ...oldHeaders,
        Authorization: `JWT ${access_token}`,
      },
    });
  }
  return forward(operation);
});

/** ТЗ 0074: ровно это сообщение сервер отдаёт приостановленному студенту. */
export const LMS_ACCESS_SUSPENDED = 'LMS_ACCESS_SUSPENDED';

/**
 * ТЗ 0074: сервер закрыл студенту LMS. Не разлогиниваем и не уводим на
 * /error — ставим флаг и перечитываем `me` из сети. Флаг выставляется ДО того,
 * как ошибку получит компонент (errorLink синхронен), поэтому гард маршрутов и
 * useGoToErrorPage уже знают, что это приостановка, и ведут на /invoices.
 *
 * Флаг снимается, когда перечитанный `me` лёг в кеш (или запрос упал): дальше
 * решает сам `me`. Пока перечитка в полёте, повторные ошибки второй запрос не
 * порождают.
 */
export const handleAccessSuspended = () => {
  if (accessSuspendedVar()) {
    return;
  }
  accessSuspendedVar(true);
  const release = () => {
    accessSuspendedVar(false);
  };
  client.query({ query: MeDocument, fetchPolicy: 'network-only' }).then(release, release);
};

const isSuspendedNetworkError = (networkError: any): boolean =>
  Boolean(networkError) &&
  (networkError.result === LMS_ACCESS_SUSPENDED || networkError.bodyText === LMS_ACCESS_SUSPENDED);

export const errorLink = onError(({ graphQLErrors, networkError }) => {
  const isSuspended =
    Boolean(graphQLErrors?.some((err) => err.message === LMS_ACCESS_SUSPENDED)) ||
    isSuspendedNetworkError(networkError);
  if (isSuspended) {
    handleAccessSuspended();
  }
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const currentState = authState();

      // TODO: fix this solution for err.extensions.code

      const ErrorDecodingSignature = err.message === 'Error decoding signature';
      const AcessTokenIsExpired = err.message === 'Signature has expired';
      const AcessTokenNoValid = err.message === 'NOT_ACCESS_TO_LMS';
      if (AcessTokenIsExpired) {
        authState({ ...currentState, isTokenRefreshed: false });
      }
      if (ErrorDecodingSignature || AcessTokenNoValid) {
        authState({ ...currentState, isLoggedIn: null });
      }
    }
  }
  // Приостановка — не сбой сервера: страницу ошибки не показываем.
  if (networkError && !isSuspended) {
    if ('result' in networkError) {
      const { statusCode } = networkError;
      const currentState = appState();
      switch (statusCode) {
        case 500:
          appState({
            ...currentState,
            networkError: NetworkErrors.internalServerError,
          });
          break;
        case 503:
          appState({
            ...currentState,
            networkError: NetworkErrors.serviceUnavailable,
          });
          break;
        case 404:
          appState({
            ...currentState,
            networkError: NetworkErrors.pageIsNotAvailable,
          });
          break;
        default:
          console.log({ networkError });
          appState({
            ...currentState,
            networkError: NetworkErrors.otherError,
          });
          break;
      }
    }
  }
});

export const client = new ApolloClient({
  link: from([errorLink, middlewareLink, restLink, uploadLink as any]),
  cache,
  connectToDevTools: true,
});
